import { useEffect, useId, useRef, type ReactNode } from "react";
import { hintId } from "./control.ts";
import { FieldMessage } from "./Field.tsx";

/**
 * Choices: a checkbox, a set of radio buttons, and a segmented control.
 *
 * Each is drawn on the real input (`appearance-none`), not on a div beside a hidden one, so the
 * keyboard, the focus ring and the screen reader all work as the browser made them. The box and the
 * dot are tactile like the rest of the UI: sunken when empty, raised in the brand blue when chosen.
 */

/** The tick and the dash, drawn over the box. White on the blue fill. */
function Tick() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="pointer-events-none absolute inset-0 m-auto hidden size-3.5 text-inverse peer-checked:block peer-indeterminate:hidden"
    >
      <path d="M4.5 10.5 8.5 14.5 15.5 6" />
    </svg>
  );
}

function Dash() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      className="pointer-events-none absolute inset-0 m-auto hidden size-3.5 text-inverse peer-indeterminate:block"
    >
      <path d="M5 10h10" />
    </svg>
  );
}

/** Sunken when empty; chosen (or "some chosen"), a small primary button (`.sk-check` in styles.css). */
const BOX = [
  "sk-check peer size-5 shrink-0 cursor-pointer appearance-none rounded-md border transition-colors duration-150",
  "focus:outline-none focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-accent-soft",
  "disabled:cursor-not-allowed disabled:opacity-45",
].join(" ");

export type CheckboxProps = {
  /** The words beside the box, or the accessible name alone when `hideLabel` is set. */
  label: string;
  /** Drawn before the words: a coach's face (`Avatar`). Decorative, the words name the box. */
  face?: ReactNode;
  hint?: string;
  error?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Some, not all: the "select all" box over a list where part of it is chosen. */
  indeterminate?: boolean;
  disabled?: boolean;
  /** The bare box with no words, for a table cell whose column header names it. */
  hideLabel?: boolean;
  /**
   * `compact`: a shorter line and 14px words, for a few boxes side by side in a table row (the roles
   * of a person in the WhatsApp sync tool). The box itself keeps its size, so it is as easy to hit.
   */
  size?: "regular" | "compact";
  name?: string;
  value?: string;
};

/**
 * A checkbox with its words beside it. The whole line is the target: the label wraps the box, so a
 * tap on the words ticks it too.
 */
export function Checkbox({
  label,
  face,
  hint,
  error,
  checked,
  onChange,
  indeterminate = false,
  disabled = false,
  hideLabel = false,
  size = "regular",
  name,
  value,
}: CheckboxProps) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);

  // `indeterminate` is a property, not an attribute: React cannot set it, so the effect does.
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const box = (
    <span className="relative inline-flex size-5 shrink-0">
      <input
        ref={ref}
        id={id}
        type="checkbox"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={hideLabel ? label : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={!hideLabel && (error || hint) ? hintId(id) : undefined}
        className={`${BOX} ${error ? "border-cancelled" : "border-line-strong"}`}
      />
      <Tick />
      <Dash />
    </span>
  );

  if (hideLabel) return box;

  return (
    <div className="flex flex-col">
      <label
        htmlFor={id}
        className={[
          "flex cursor-pointer items-start has-disabled:cursor-not-allowed has-disabled:opacity-60",
          size === "compact" ? "min-h-8 gap-2 py-1.5" : "min-h-11 gap-3 py-2.5",
        ].join(" ")}
      >
        <span className="mt-px">{box}</span>
        <span className={`min-w-0 flex-1 leading-5 font-semibold text-ink ${size === "compact" ? "text-[14px]" : "text-[15px]"}`}>
          {face ? <span className="mr-1.5 inline-flex align-middle">{face}</span> : null}
          {label}
        </span>
      </label>
      {/* Under the words, not inside the label: it describes the box, it does not name it. */}
      {hint || error ? (
        <div className="-mt-1.5 pb-2 pl-8">
          <FieldMessage id={id} hint={hint} error={error} />
        </div>
      ) : null}
    </div>
  );
}

export type RadioOption<T extends string> = {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
};

/**
 * One choice from a short list, all of it visible: "Pay now" or "Pay at the front desk". For a long
 * list use `SelectField`; for two or three short words on one line use `SegmentedControl`.
 *
 * A fieldset with a legend, so a screen reader names the group before each option.
 */
export function RadioGroup<T extends string>({
  legend,
  hint,
  error,
  options,
  value,
  onChange,
  name,
  hideLegend = false,
}: {
  legend: string;
  hint?: string;
  error?: string;
  options: RadioOption<T>[];
  /** The chosen value, or null when nothing is chosen yet. */
  value: T | null;
  onChange: (value: T) => void;
  /** The radio `name`. A unique one is made when left out. */
  name?: string;
  hideLegend?: boolean;
}) {
  const id = useId();
  const groupName = name ?? id;
  const described = error || hint ? hintId(id) : undefined;

  return (
    <fieldset className="flex min-w-0 flex-col gap-1" aria-describedby={described}>
      <legend className={hideLegend ? "sr-only" : "eyebrow mb-1 text-ink-2"}>{legend}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          className="flex min-h-11 cursor-pointer items-start gap-3 py-2.5 has-disabled:cursor-not-allowed has-disabled:opacity-60"
        >
          <input
            type="radio"
            name={groupName}
            value={option.value}
            checked={value === option.value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
            className={[
              "sk-field mt-px size-5 shrink-0 cursor-pointer appearance-none rounded-full border",
              "transition-[border-width,border-color] duration-150",
              "checked:border-[6px] checked:border-accent checked:shadow-[0_1px_2px_rgba(28,68,157,0.35)]",
              "focus:outline-none focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-accent-soft",
              "disabled:cursor-not-allowed",
              error ? "border-cancelled" : "border-line-strong",
            ].join(" ")}
          />
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] leading-5 font-semibold text-ink">{option.label}</span>
            {option.hint ? <span className="mt-0.5 block text-[13px] text-ink-2">{option.hint}</span> : null}
          </span>
        </label>
      ))}
      <FieldMessage id={id} hint={hint} error={error} />
    </fieldset>
  );
}

export type SwatchOption<T extends string> = {
  value: T;
  /** The colour word a screen reader says: "Red". */
  label: string;
  /** Any CSS colour: a hex value or a token, `var(--color-accent)`. */
  color: string;
};

/**
 * A colour from a fixed palette: one native radio per colour, drawn as a round swatch with a ring
 * on the chosen one. The radios keep the arrow keys and say "Red, selected" to a screen reader.
 * The colour is a swatch only, never under text, so any palette keeps the text contrast.
 */
export function SwatchPicker<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false,
  name,
}: {
  /** The group's accessible name, not shown: "Colour of New". */
  label: string;
  options: SwatchOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** The radio `name`. A unique one is made when left out. */
  name?: string;
}) {
  const id = useId();
  return (
    <fieldset disabled={disabled} className="flex min-w-0 flex-wrap gap-1.5 disabled:opacity-45">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => (
        <label key={option.value} title={option.label} className="cursor-pointer has-disabled:cursor-not-allowed">
          <input
            type="radio"
            name={name ?? id}
            value={option.value}
            checked={value === option.value}
            aria-label={option.label}
            onChange={() => onChange(option.value)}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={[
              "grid size-8 place-items-center rounded-full border-2 border-transparent transition-colors duration-150 hover:border-line-strong",
              "peer-checked:border-ink peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-soft",
            ].join(" ")}
          >
            {/* A thin inner ring, so a very light colour (platinum) still shows on white. */}
            <span className="block size-5 rounded-full shadow-[inset_0_0_0_1px_rgba(17,26,43,0.14)]" style={{ background: option.color }} />
          </span>
        </label>
      ))}
    </fieldset>
  );
}

export type SegmentTone = "neutral" | "confirmed" | "late";

export type SegmentOption<T extends string | boolean> = {
  value: T;
  label: string;
  /**
   * The colour of the segment when it is chosen. `neutral` (white, raised) by default; `confirmed`
   * and `late` for a choice that is a status, like "Paid" and "Owed".
   */
  tone?: SegmentTone;
};

/** The chosen segment: raised white, or a status choice ("Paid", "Owed") as a small button in its tone. */
const SEGMENT_ON: Record<SegmentTone, string> = {
  neutral: "sk-seg-on",
  confirmed: "sk-tile [--sk-p:var(--color-confirmed)] text-inverse",
  late: "sk-tile [--sk-p:var(--color-late)] text-inverse",
};

/**
 * Two or three short choices side by side, one always chosen: "Paid" or "Owed", "Week" or "Month".
 * The track is sunken and the chosen segment is raised out of it.
 *
 * Radio buttons underneath, so the arrow keys move the choice and a screen reader reads "1 of 2".
 */
export function SegmentedControl<T extends string | boolean>({
  label,
  options,
  value,
  onChange,
  size = "regular",
  name,
}: {
  /** The accessible name of the group, not shown: "Paid or owed: Aisha Al Mansoori". */
  label: string;
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "regular" | "compact";
  name?: string;
}) {
  const id = useId();
  const groupName = name ?? id;

  return (
    <fieldset className="sk-track relative inline-flex min-w-0 gap-1 rounded-xl border border-line p-1">
      <legend className="sr-only">{label}</legend>
      {options.map((option) => {
        const on = option.value === value;
        return (
          <label
            key={String(option.value)}
            className={[
              "flex min-w-16 cursor-pointer items-center justify-center rounded-lg px-3 font-semibold transition-colors duration-150",
              "has-focus-visible:outline-3 has-focus-visible:outline-accent-soft",
              size === "regular" ? "h-11 text-[15px]" : "h-8 text-[13px]",
              on ? SEGMENT_ON[option.tone ?? "neutral"] : "text-ink-2 hover:text-ink",
            ].join(" ")}
          >
            <input
              type="radio"
              className="sr-only"
              name={groupName}
              checked={on}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </label>
        );
      })}
    </fieldset>
  );
}
